<template>
  <div class="col-md-12 d-flex align-items-center justify-content-center" style="height:100vh">
    <div class="card card-container">
      <img
        id="profile-img"
        src="@/assets/images/user.svg"
        class="bg-image profile-img-card"
      />
      <!-- Step 1: credentials -->
      <Form v-if="step === 1" @submit="handleSubmit" :validation-schema="schema">
        <div class="form-group">
          <label for="username">Brukernavn</label>
          <Field id="username" name="username" type="text" class="form-control" />
          <ErrorMessage name="username" class="error-feedback" />
        </div>
        <div class="form-group">
          <label for="password">Passord</label>
          <div style="position:relative">
            <Field id="password" name="password" v-model="passwordInput" :type="showPassword ? 'text' : 'password'" class="form-control" style="padding-right:38px" />
            <button type="button" @click="showPassword = !showPassword"
                    :title="showPassword ? 'Skjul passord' : 'Vis passord'"
                    style="position:absolute; right:10px; top:50%; transform:translateY(-50%); border:none; background:none; color:var(--bo-muted); cursor:pointer; padding:0; line-height:1;">
              <font-awesome-icon :icon="['fas', showPassword ? 'eye-slash' : 'eye']" />
            </button>
          </div>
          <ErrorMessage name="password" class="error-feedback" />
          <!-- Warn, never mutate: a space is a valid password character, and the same string is also
               the merchant's Sync Gateway credential, so trimming it here would silently diverge from
               what the terminals authenticate with. Just make the invisible visible and let them decide. -->
          <div v-if="passwordWhitespaceHint" class="whitespace-hint">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="whitespace-hint__icon" />
            <span>{{ passwordWhitespaceHint }}</span>
          </div>
        </div>

        <div class="form-group">
          <button class="btn btn-bg btn- btn-block mt-4" style="width:100%" :disabled="loading">
            <span
              v-show="loading"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            <span>Logg inn</span>
          </button>
        </div>

        <div class="form-group">
          <div v-if="message" class="alert alert-danger mt-1" role="alert">
            {{ message }}
          </div>
        </div>
      </Form>

      <!-- Step 2: SMS one-time code (two-factor) -->
      <form v-else @submit.prevent="verifyOtp">
        <p class="mt-2" style="font-size:0.9rem">
          Vi sendte en 6-sifret kode på SMS til <strong>{{ phoneHint }}</strong>.
        </p>
        <div class="form-group">
          <label for="code" class="code-label">SMS-kode</label>
          <!-- Six boxes, one digit each. They are drawn UNDER one real input rather than being six
               inputs: a single field keeps the SMS autofill, paste and maxlength behaviour that six
               would each have to re-implement, and there is no focus to shuffle between them. The
               input is transparent and covers the whole row, so a tap anywhere lands in it. -->
          <div class="code-boxes" :class="{ 'is-invalid': codeError }">
            <input id="code" ref="codeInput" type="text" inputmode="numeric" autocomplete="one-time-code"
                   maxlength="6" class="code-boxes-input" v-model="code"
                   @input="onCodeInput" @click="onCodeClick" @keyup="syncCaret"
                   @focus="codeFocused = true" @blur="codeFocused = false" />
            <span v-for="i in 6" :key="i" class="code-box"
                  :class="{ 'is-active': codeFocused && i - 1 === activeCodeBox, 'is-filled': !!code[i - 1] }"
                  aria-hidden="true">{{ code[i - 1] || '' }}</span>
          </div>
        </div>

        <div class="form-group">
          <button class="btn btn-bg btn- btn-block mt-4" style="width:100%" :disabled="loading || code.length < 6">
            <span v-show="loading" class="spinner-border spinner-border-sm me-1"></span>
            <span>Bekreft</span>
          </button>
        </div>

        <div class="form-group" style="text-align:center; font-size:0.85rem">
          <a v-if="resendCooldown === 0" href="#" @click.prevent="resendCode">Send ny kode</a>
          <span v-else style="color:#aaa">Send ny kode ({{ resendCooldown }}s)</span>
          <span style="margin:0 8px; color:var(--bo-muted-2)">|</span>
          <a href="#" @click.prevent="backToLogin">Tilbake</a>
        </div>

        <div class="form-group">
          <div v-if="info" class="alert alert-success mt-1 py-2" role="alert">{{ info }}</div>
          <div v-if="message" class="alert alert-danger mt-1" role="alert">{{ message }}</div>
        </div>
      </form>
    </div>
  </div>

  <div class="col-md-12 d-flex align-items-center justify-content-center text-center">
    <div style="color: rgba( 112, 123, 124, 0.9);">
          <span>
              Nettstedet er beskyttet av reCaptcha, se Googles <a href="https://policies.google.com/privacy?hl=no">personvern</a> og <a href="https://policies.google.com/terms?hl=no">vilkår</a>
          </span>
    </div>
  </div>
</template>

<script>
  import Recaptcha from "@/services/auth/Recaptcha";
  import { Form, Field, ErrorMessage } from "vee-validate";
  import * as yup from "yup";
  import MerchantUtil from "@/util/MerchantUtil";

  // Script loading and token minting live in services/auth/Recaptcha.js — one copy, two logins.

  export default {
    setup() {
      Recaptcha.preload();   // warm the script while credentials are typed; no-op in dev
    },
    components: {
      Form,
      Field,
      ErrorMessage,
    },
    data() {
      const schema = yup.object().shape({
        username: yup.string().required("Fyll ut brukernavn"),
        password: yup.string().required("Fyll ut passord"),
      });

      return {
        loading: false,
        message: "",
        info: "",
        schema,
        showPassword: false,
        passwordInput: "",
        step: 1,
        phoneHint: "",
        code: "",
        codeFocused: false,  // the boxes draw their own caret, so they need to know
        codeError: false,    // last attempt was rejected - shown until the next keystroke
        caretIndex: 0,       // where the caret really is, so the drawn one can follow it
        // Held in memory between the two steps; the backend re-validates them on verify.
        creds: { username: "", password: "" },
        resendCooldown: 0,   // seconds left before "Send ny kode" is clickable again
        resendTimer: null,
      };
    },
    computed: {
      loggedIn() {
        return this.$store.state.auth.status.loggedIn;
      },
      /** Which box the caret is drawn in. It follows the real caret, so it lands on the box that was
       *  clicked or arrowed to, not just on the end of what has been typed. */
      activeCodeBox() {
        // Clamped to what is typed as well, so a caret left over from a longer code (cleared on a
        // rejected attempt, or on "Send ny kode") cannot ring a box that no longer holds anything.
        return Math.min(Math.max(this.caretIndex, 0), 5, this.code.length);
      },
      /**
       * Merchants paste the temporary password out of the welcome email and routinely bring a space
       * (or a non-breaking space / newline) along with it — invisible even with "vis passord" on, so
       * the login just fails and they have no idea why. Surface it; do NOT strip it. \s covers NBSP,
       * tabs and newlines.
       */
      passwordWhitespaceHint() {
        const pw = this.passwordInput || "";
        if (!pw) return "";
        const leading = /^\s/.test(pw);
        const trailing = /\s$/.test(pw);
        if (!leading && !trailing) return "";
        const where = leading && trailing ? "starter og slutter" : (leading ? "starter" : "slutter");
        return "Passordet " + where + " med mellomrom \u2014 ble det med da du limte inn?";
      },
    },
    // No sign-out on arrival. A signed-in user never reaches this page: router.beforeEach sends them to
    // Oversikt first, and the reasoning — including why this page once DID sign people out on arrival —
    // lives on that guard.
    methods: {
      /**
       * Mint a reCAPTCHA token and send it WITH the credentials.
       *
       * This used to ask the backend to score the token first and call the login only if it passed —
       * which protects nothing, because a script never makes the first call. backoffice_login now
       * verifies the token itself, before it reads a username, so the check is on the door rather than
       * in the hand of whoever knocks.
       *
       * Recaptcha.execute resolves to null in DEV (the site key does not cover localhost); the backend's
       * config.recaptchaEnforceLogin must be off there, and defaults to on everywhere else.
       */
      handleSubmit(user) {
        var self = this;
        this.loading = true;
        this.message = "";

        Recaptcha.execute('login').then(
          function(token) {
            self.login({ ...user, recaptchaToken: token });
          },
          function(error) {
            console.log(error);
            self.loading = false;
            self.message = "Kunne ikke laste reCaptcha. Sjekk nettverket eller annonseblokkering, og prøv igjen.";
          }
        );
      },
      login(user){
        if(user){
          this.creds = { username: user.username, password: user.password, recaptchaToken: user.recaptchaToken };
        }
        this.message = "";
        this.$store.dispatch("auth/login", this.creds).then(
            (res) => {
              this.loading = false;
              if(res.result === "OTP_REQUIRED"){
                this.phoneHint = res.phoneHint;
                this.step = 2;
                this.startResendCooldown();
              } else if(res.result === "SUCCEEDED"){
                this.goAfterLogin();
              } else {
                this.message = res.message || "Feil brukernavn/passord";
              }
            },
            (error) => {
              this.loading = false;
              this.message = "Feil brukernavn/passord";
              console.log("Innlogging mislyktes: " + JSON.stringify(error));
            }
        );
      },
      verifyOtp(){
        this.loading = true;
        this.message = "";
        this.info = "";
        this.$store.dispatch("auth/verifyLogin", { ...this.creds, code: this.code }).then(
            (res) => {
              this.loading = false;
              if(res.result === "SUCCEEDED"){
                this.goAfterLogin();
              } else {
                this.message = res.message || "Feil eller utløpt kode.";
                this.resetCodeForRetry();
              }
            },
            () => {
              this.loading = false;
              this.message = "Verifisering mislyktes";
              this.resetCodeForRetry();
            }
        );
      },
      /** A fresh attempt clears the last one's verdict, so neither the red boxes nor the message
       *  linger over digits they no longer describe. */
      onCodeInput(){
        this.codeError = false;
        this.message = "";
        this.syncCaret();
      },
      /** The drawn caret is only right if it knows where the real one went - after a keystroke, an
       *  arrow key, or a click. */
      syncCaret(){
        const input = this.$refs.codeInput;
        if(input) this.caretIndex = input.selectionStart;
      },
      /** Click or tap a box to correct that digit. The boxes sit under the input, so the click lands
       *  on the input and the box is worked out from where along the row it hit: the six slices are
       *  equal, and every boundary between them falls inside a gap, never inside a box. The digit
       *  there is SELECTED rather than just pointed at, so the next keystroke replaces it - with six
       *  digits in a six-character field an insert would be swallowed by maxlength. */
      onCodeClick(event){
        const input = this.$refs.codeInput;
        if(!input) return;
        const rect = input.getBoundingClientRect();
        const box = Math.floor((event.clientX - rect.left) / (rect.width / 6));
        // Never past the end of what is typed: box 5 is not editable while only two digits exist.
        const at = Math.min(Math.max(box, 0), 5, this.code.length);
        input.setSelectionRange(at, at < this.code.length ? at + 1 : at);
        this.caretIndex = at;
      },
      /** A rejected code leaves six digits in a field that accepts six, so every further keystroke
       *  would be swallowed by maxlength and the panel would look stuck. Empty it, mark it, and put
       *  the caret back in the first box so the next attempt is just typing. */
      resetCodeForRetry(){
        this.codeError = true;
        this.code = "";
        this.$nextTick(() => {
          if(this.$refs.codeInput) this.$refs.codeInput.focus();
        });
      },
      resendCode(){
        if(this.loading || this.resendCooldown > 0) return;
        this.loading = true;
        this.message = "";
        this.info = "";
        this.code = "";
        this.$store.dispatch("auth/login", this.creds).then(
            (res) => {
              this.loading = false;
              if(res.result === "OTP_REQUIRED"){
                this.phoneHint = res.phoneHint;
                this.info = "Ny kode sendt.";
                this.startResendCooldown();
              } else if(res.result === "SUCCEEDED"){
                this.goAfterLogin();
              } else {
                this.message = res.message || "Kunne ikke sende ny kode.";
              }
            },
            () => {
              this.loading = false;
              this.message = "Kunne ikke sende ny kode.";
            }
        );
      },
      backToLogin(){
        this.step = 1;
        this.code = "";
        this.message = "";
        this.info = "";
        this.clearResendTimer();
        this.resendCooldown = 0;
      },
      startResendCooldown(){
        this.clearResendTimer();
        this.resendCooldown = 30;
        this.resendTimer = setInterval(() => {
          this.resendCooldown--;
          if(this.resendCooldown <= 0){
            this.clearResendTimer();
          }
        }, 1000);
      },
      clearResendTimer(){
        if(this.resendTimer){
          clearInterval(this.resendTimer);
          this.resendTimer = null;
        }
      },
      /**
       * Enter the app with a FULL page load, not a router push.
       *
       * A push keeps the running SPA alive across a tenant switch: whatever the previous merchant's
       * session left in module scope, in Vuex, or in a component instance the router happens to reuse
       * is still there, and the landing page can come up showing the old merchant's numbers. Reloading
       * costs one request and removes the whole class — there is no such thing as a leftover when the
       * app starts from nothing.
       */
      goAfterLogin(){
        this.clearResendTimer();
        const target = MerchantUtil.useFunctionality() === 'WEBSITE' ? '/products' : '/overview';
        window.location.assign(target);
      }
    },
    unmounted(){
      this.clearResendTimer();
    },
  };
</script>

<style scoped>
  label {
    display: block;
    margin-top: 10px;
  }

  .card-container.card {
    max-width: 350px !important;
    padding: 0px 40px 40px 40px;
  }

  .card {
    background-color: var(--bo-surface-2);
    padding: 20px 25px 30px;
    margin: 0 auto 25px;
    border-radius: 12px;
    -moz-box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.3);
    -webkit-box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.3);
    box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.3);
  }

  /* The figure in user.svg is white, baked into the file, so the disc behind it must stay dark in BOTH
     themes. It was var(--bo-ink), which the dark theme turns near-white (#e8eaee): a white figure on a
     white disc. */
  .bg-image {
    background-color: #1a1a2e;
    color: var(--bo-surface);
  }
  /* On the dark card a navy disc disappears into it; a lifted grey still reads as a disc and keeps the
     figure at about 10:1. */
  [data-bs-theme="dark"] .bg-image {
    background-color: #3a404a;
  }

  .btn-bg {
    background-color: #ECB109;
    color: #fff;
  }

  .profile-img-card {
    width: 96px;
    height: 96px;
    margin: 0 auto -20px;
    transform: translate(0px, -50px);
    display: block;
    -moz-border-radius: 50%;
    -webkit-border-radius: 50%;
    border-radius: 50%;
  }

  .error-feedback {
    color: var(--bo-alert-ink);
  }

  /* Caution, not an error — deliberately NOT the red of .error-feedback, since a space is a legal
     password character and the merchant may have meant it. #8A6800 is the same dark amber as
     bo_btn_secondary's text, and holds contrast against the card's var(--bo-surface-2). */
  .whitespace-hint {
    display: flex;
    /* Sit the icon on the text's baseline, the way an inline glyph would. flex-start pinned it to the
       top of the line box instead, which is above the letters - the line is 1.35em tall and the
       letters only fill the middle of it. Flex keeps the second line indented past the icon. */
    align-items: baseline;
    gap: 6px;
    margin-top: 6px;
    color: var(--bo-brand-ink);
    font-size: 0.8rem;
    line-height: 1.35;
  }

  /* An SVG has no baseline of its own, so flex aligns its BOTTOM edge to the text's. This is the same
     -0.125em nudge Font Awesome applies inline (as vertical-align, which flex items ignore) to settle
     the glyph onto the line rather than perch it on top. */
  .whitespace-hint__icon {
    flex: none;
    margin-bottom: -0.125em;
  }

  /* Centred over the row of boxes it labels. */
  .code-label {
    text-align: center;
  }

  /* One box per digit, the way an SMS code is read. The real input sits on top, transparent and
     full-width, so autofill/paste keep working and a tap anywhere focuses it; these boxes draw the
     digits and the caret. */
  .code-boxes {
    position: relative;
    display: flex;
    gap: 0.4rem;
  }

  .code-boxes-input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    padding: 0;
    border: none;
    background: transparent;
    color: transparent;
    caret-color: transparent;
    font-size: 1.4rem;
    z-index: 1;
    cursor: text;
  }

  .code-boxes-input:focus {
    outline: none;
  }

  /* A selection would otherwise paint a blue block over the invisible text. */
  .code-boxes-input::selection {
    background: transparent;
  }

  .code-box {
    flex: 1 1 0;
    aspect-ratio: 1 / 1;
    min-height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bo-surface);
    border: 1px solid var(--bo-muted-2);
    border-radius: 8px;
    font-size: 1.4rem;
    font-variant-numeric: tabular-nums;
    color: var(--bo-ink);
    transition: border-color 0.2s ease;
  }

  /* The box the next digit lands in — ringed in the brand amber; the extra ring reads as a 2px
     border without shifting the row. */
  .code-box.is-active {
    border-color: #ECB109;
    box-shadow: 0 0 0 1px #ECB109;
  }

  /* A rejected code. The boxes are emptied for the next attempt (see resetCodeForRetry), and stay
     red until typing starts. */
  .code-boxes.is-invalid .code-box {
    border-color: #B30E10;
  }

  .code-boxes.is-invalid .code-box.is-active {
    box-shadow: 0 0 0 1px #B30E10;
  }

  /* The caret the transparent input no longer shows. */
  .code-box.is-active:not(.is-filled)::after {
    content: "";
    width: 2px;
    height: 1.3rem;
    background: var(--bo-ink);
    animation: code-caret 1s step-end infinite;
  }

  @keyframes code-caret {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .code-box.is-active:not(.is-filled)::after {
      animation: none;
    }
  }
</style>
